import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PricingSection } from '@/components/PricingSection';
import { ProcessSlider } from '@/components/ProcessSlider';
import { TestimonialSlider } from '@/components/TestimonialSlider';
import { ServicesSlider } from '@/components/ServicesSlider';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function Home({ onOpenPopup }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>Trademark Venus - Professional Trademark Registration Services in USA</title>
        <meta name="description" content="Protect your brand with comprehensive trademark registration, copyright registration, and legal services. Fast, affordable, and expert trademark filing starting at $45. Trusted by thousands of businesses across the USA." />
        <meta name="keywords" content="trademark registration, copyright registration, trademark search, brand protection, USPTO filing, trademark attorney, intellectual property" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 opacity-90" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-red-400 mb-4 block">
                Trusted Trademark Services
              </span>
              <h1 className="text-5xl md:text-7xl font-heading font-bold text-white leading-tight mb-6">
                Protect Your Brand with{' '}
                <span className="bg-gradient-to-r from-red-400 to-red-300 bg-clip-text text-transparent">
                  Confidence
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                Comprehensive trademark registration and legal services designed to safeguard your business identity across the United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onOpenPopup}
                  data-testid="hero-get-started-button"
                  className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-red-500/30 transition-all duration-300 hover:-translate-y-1"
                >
                  Get Started Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  data-testid="hero-explore-button"
                  className="border-slate-200 text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
                >
                  Explore Services
                </Button>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-red-600 to-red-400 rounded-3xl blur-3xl opacity-20" />
                <img
                  src="https://images.unsplash.com/photo-1758518726869-01aff69a56e3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY5NDYyMjIyfDA&ixlib=rb-4.1.0&q=85"
                  alt="Professional business meeting"
                  className="relative rounded-3xl shadow-2xl border border-slate-200 w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBidXNpbmVzcyUyMG1lZXRpbmclMjBwcm9mZXNzaW9uYWx8ZW58MHx8fHwxNzY5NDYyMjIyfDA&ixlib=rb-4.1.0&q=85"
                alt="Team collaboration"
                className="rounded-3xl shadow-xl border border-slate-200 w-full h-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Your Trusted Partner in Brand Protection
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                At Trademark Venus, we understand that your brand is more than just a name—it's your reputation, your identity, and your future. Our expert team specializes in providing comprehensive trademark and legal services that protect what matters most to your business.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                With years of experience and thousands of successful registrations, we've helped businesses across the United States secure their intellectual property and build lasting brand value.
              </p>
              <Button
                onClick={onOpenPopup}
                data-testid="info-section-button"
                className="bg-gradient-to-r from-red-700 to-red-600 hover:from-red-800 hover:to-red-700 text-white font-semibold rounded-full px-8 py-4 shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                Start Your Registration
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
              Our Services
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Complete Brand Protection Solutions
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From trademark registration to comprehensive legal services, we've got you covered
            </p>
          </motion.div>

          <ServicesSlider onOpenPopup={onOpenPopup} />
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Simple, Fast, and Secure
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We've streamlined the trademark registration process into three easy steps
            </p>
          </motion.div>

          <ProcessSlider />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-red-700 mb-4 block">
              Client Success
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Trusted by Businesses Nationwide
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              See what our clients have to say about their experience
            </p>
          </motion.div>

          <TestimonialSlider />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-gradient-to-r from-red-900 via-red-700 to-red-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.8),transparent_50%)]" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              Ready to Protect Your Brand?
            </h2>
            <p className="text-red-100 text-lg md:text-xl max-w-2xl mx-auto mb-8">
              Join thousands of businesses that trust us with their trademark registration. Get started today and secure your brand's future.
            </p>
            <Button
              onClick={onOpenPopup}
              data-testid="cta-section-button"
              className="bg-white text-red-700 hover:bg-red-50 font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              Start Your Application Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
